/**
 * Mapping between the assistant_templates API and the avatar form state.
 *
 * Two quirks of the API live here, and nowhere else:
 *
 * 1. Blocks and examples are the SAME backend entity, told apart by
 *    `section_type`. We split them on read and merge them back — sharing one
 *    `order` counter — on write.
 * 2. The API *returns* camelCase (`outputFormat`, `sectionType`) but *accepts*
 *    snake_case, and its `structured_json` mode is our `json`.
 *
 * Everything in this file is pure: no React, no network.
 */

import { nanoid } from "nanoid";

/** Short labels for the output mode, as shown on cards and chips. */
const OUTPUT_FORMAT_LABEL = {
  structured_json: "JSON",
  json: "JSON",
  markdown: "MD",
  html: "HTML",
  normal: "Text",
};

/** The backend calls it `structured_json`; the form calls it `json`. */
export const normalizeOutputMode = (mode) =>
  mode === "structured_json" ? "json" : mode || "normal";

/** Reads the block count off either a summary row or a full template. */
export const sectionsOf = (t) =>
  t?.sectionsCount ?? t?.sections_count ?? t?.sections?.length ?? 0;

/** Reads the output mode off either casing, as a display label. */
export const outputFormatOf = (t) => {
  const mode = t?.outputFormat?.mode ?? t?.output_format?.mode ?? "normal";
  return OUTPUT_FORMAT_LABEL[mode] || "Text";
};

/** The JSON schema is returned under either casing. */
export const jsonSchemaOf = (t) =>
  t?.outputFormat?.jsonSchema ?? t?.output_format?.jsonSchema ?? null;

/**
 * One API section → the shape SectionsManager edits.
 *
 * SectionsManager reads `title`/`markdown`; the read-only views read
 * `name`/`content`. We carry all of them so both work off one object.
 */
const toFormSection = (section, fallbackTitle) => ({
  id: section.id || nanoid(),
  title: section.name || fallbackTitle,
  name: section.name || fallbackTitle,
  html: section.content || "",
  markdown: section.content || "",
  content: section.content || "",
  order: section.order || 0,
  icon: section.icon || null,
});

/**
 * Split the flat `sections` array the API returns into the two lists the form
 * keeps apart.
 */
export const splitTemplateSections = (allSections = []) => {
  const byType = (type, fallbackTitle) =>
    allSections
      .filter((s) => (s.sectionType || s.section_type) === type)
      .sort((a, b) => a.order - b.order)
      .map((s) => toFormSection(s, fallbackTitle));

  return {
    sections: byType("section", "Untitled"),
    examples: byType("example", "Example"),
  };
};

/**
 * Merge blocks and examples back into the single array the API takes.
 *
 * `order` is a single counter across both lists — blocks first, then examples —
 * because they land in the same table.
 */
export const toSectionsPayload = (sections = [], examples = []) => {
  const payload = [];

  const push = (item, index, type, fallbackTitle) =>
    payload.push({
      name: item.title || item.name || `${fallbackTitle} ${index + 1}`,
      content: item.markdown || item.content || "",
      order: payload.length + 1,
      section_type: type,
      // Only sent when the user actually picked one. The assistant service
      // forbids unknown fields, and `icon` only exists there once zenitailab
      // !854 ships — sending `icon: null` 422s every save until then.
      ...(item.icon ? { icon: item.icon } : {}),
    });

  sections.forEach((s, i) => push(s, i, "section", "Section"));
  examples.forEach((e, i) => push(e, i, "example", "Example"));

  return payload;
};

/** JSON schema (any of the shapes the API has used) → the visual builder's fields. */
export const jsonSchemaToFields = (schema) => {
  if (!schema || typeof schema !== "object") return [];

  // Shapes seen in the wild, in order of specificity.
  const actualSchema =
    schema.json_schema?.schema || schema.schema || (schema.properties ? schema : {});

  const properties = actualSchema.properties || {};
  const required = actualSchema.required || [];

  return Object.entries(properties).map(([name, prop]) => ({
    id: nanoid(),
    name,
    type: prop.type || "string",
    description: prop.description || "",
    required: required.includes(name),
  }));
};

/** The visual builder's fields → the JSON schema the API stores. */
export const fieldsToJsonSchema = (fields) => {
  if (!fields || fields.length === 0) return null;

  const properties = {};
  const required = [];

  fields.forEach((field) => {
    if (!field.name || !field.name.trim()) return;
    properties[field.name] = {
      type: field.type || "string",
      ...(field.description ? { description: field.description } : {}),
    };
    if (field.required) required.push(field.name);
  });

  return {
    type: "json_schema",
    json_schema: {
      name: "custom_output",
      strict: true,
      schema: {
        type: "object",
        properties,
        ...(required.length > 0 ? { required } : {}),
      },
    },
  };
};

/** Peel the schema out of whichever wrapper the API used. */
const unwrapSchema = (schema) => {
  if (!schema || typeof schema !== "object") return null;
  return schema.json_schema?.schema || schema.schema || (schema.properties ? schema : null);
};

/**
 * A sample response for a JSON schema — what the avatar's output will look
 * like, as opposed to the schema, which describes it.
 *
 * Every leaf shows its type. We know the shape of a response, never its
 * content, so naming the type is the most a preview can honestly say — and it
 * is more use than a placeholder like "…". Enums are the exception: the schema
 * names the allowed values, so showing one is a fact rather than a guess.
 */
const sampleForNode = (node) => {
  if (!node || typeof node !== "object") return null;
  if (Array.isArray(node.enum) && node.enum.length > 0) return node.enum[0];

  switch (node.type) {
    case "object": {
      const properties = node.properties || {};
      return Object.fromEntries(
        Object.entries(properties).map(([key, child]) => [key, sampleForNode(child)])
      );
    }
    case "array":
      return node.items ? [sampleForNode(node.items)] : [];
    default:
      return node.type || "string";
  }
};

/**
 * Build the example response shown in the Output tab. Returns null when there
 * is no schema to build it from.
 */
export const sampleFromJsonSchema = (schema) => {
  const actual = unwrapSchema(schema);
  if (!actual || !actual.properties) return null;
  return sampleForNode({ type: "object", properties: actual.properties });
};

/** Tags are stored lowercase-with-dashes. */
export const normalizeTag = (tag) =>
  tag
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
